import { Skeleton } from "antd";
import { Back } from "client/src/components/Back/Back";
import { Circle } from "client/src/components/Circle/Circle";
import { DueDate } from "client/src/components/DueDate/DueDate";
import { H3, Body1 } from "client/src/components/Typography/Typography";
import { useClientHubParams } from "client/src/hooks/useClientHubParams";
import { useIntersectionObserver } from "client/src/hooks/useIntersectionObserver";
import { getShouldDisplayDueDate } from "client/src/utils/getShouldDisplayDueDate";
import clsx from "clsx";
import { Link } from "react-router";
import { RouteData } from "shared/config/routeData";

import BackArrow from "../Icons/BackArrow.svg?react";

import * as styles from "./childPageLayout.module.css";
import type { JSX } from "react";

import type { Task } from "shared/data/Tasks";

export type ChildPageLayoutProps = {
  topNavTitle: string;
  task: Task | null;
  aside: JSX.Element;
  contents: JSX.Element;
  bottomToolbar?: JSX.Element;
  onClickBack?: () => void;
};

export function ChildPageLayout({
  topNavTitle,
  task,
  aside,
  contents,
  bottomToolbar,
  onClickBack,
}: ChildPageLayoutProps) {
  const { clientId } = useClientHubParams();

  const { ref: asideRef, entry } = useIntersectionObserver<HTMLDivElement>();

  const shouldDisplayDueDate = task ? getShouldDisplayDueDate(task) : false;

  return (
    <div>
      <div className={styles.layout}>
        <div
          className={clsx(styles.topNav, entry?.isIntersecting === false && styles.topNav__visible)}
          inert={!!entry?.isIntersecting}
        >
          <div className={styles.topNavInner}>
            <div>
              <Back onClick={onClickBack} to={RouteData.homeHub.getPath(clientId ?? "")}>
                Back to Home
              </Back>
            </div>

            <div className="ml-[48px]">
              <H3 as="span">{topNavTitle}</H3>
            </div>

            {shouldDisplayDueDate && task && (
              <>
                <div className="mx-[24px]">|</div>

                <DueDate task={task} variant="light" />
              </>
            )}
          </div>
        </div>

        <div className={styles.aside}>
          <div className={styles.circlePosition}>
            <Circle variant="blue" />
          </div>
          <div className={styles.circleThinPosition}>
            <Circle thin variant="blue" />
          </div>

          <div className={styles.asideInner}>
            <div className={styles.backLink} ref={asideRef}>
              <Body1>
                <Link
                  to={RouteData.homeHub.getPath(clientId ?? "")}
                  className={`stack-x-8 py-[8px] ${styles.alignCenterInline}`}
                >
                  <BackArrow />
                  <span>Back to Home</span>
                </Link>
              </Body1>
            </div>

            {aside}
          </div>
        </div>

        <div className={styles.contents}>
          <div className={styles.contentsInner}>{contents}</div>
        </div>
      </div>
      {bottomToolbar}
    </div>
  );
}

export function ChildPageSkeleton() {
  return (
    <ChildPageLayout
      topNavTitle="-"
      task={null}
      aside={
        <>
          <Skeleton active />
          <Skeleton active title={false} />
          <br />
          <Skeleton active title={false} />
        </>
      }
      contents={
        <>
          <Skeleton
            active
            paragraph={{
              rows: 6,
            }}
          />
          <br />
          <Skeleton
            active
            paragraph={{
              rows: 8,
            }}
          />
          <br />
          <Skeleton
            active
            title={false}
            paragraph={{
              rows: 8,
            }}
          />
        </>
      }
    />
  );
}
